import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import { N8AOPass } from 'n8ao';
import type { Quality } from '@dn/shared';

/**
 * v0.16 đồ họa cân bằng: thêm ánh phản chiếu môi trường (env – kim loại, kính, mặt nước có ánh đèn), bloom cho mức Trung bình
 * (đèn lồng tỏa sáng; tự tắt trước tiên khi máy chậm), khử răng cưa MSAA cả khi có hậu kỳ.
 */
export interface QualityProfile { pixelRatio: number; shadows: boolean; shadowSize: number; bloom: boolean; particleScale: number; antialias: boolean; env: number; bloomStrength: number; msaa: number; ao: false | 'half' | 'full' }
export const QUALITY: Record<Quality, QualityProfile> = {
  low: { pixelRatio: 1, shadows: false, // v0.19: vẽ đủ độ phân giải (nét) – cảnh nhẹ hơn nhiều nhờ gộp lưới; máy yếu vẫn tự hạ ở bậc giảm tải 3–5
  shadowSize: 512, bloom: false, particleScale: 0.35, antialias: false, env: 0.5, bloomStrength: 0, msaa: 0, ao: false },
  medium: { pixelRatio: 1.25, shadows: true, shadowSize: 1536, bloom: true, particleScale: 0.7, antialias: true, env: 0.8, bloomStrength: 0.42, msaa: 2, ao: 'half' },
  high: { pixelRatio: 2, shadows: true, shadowSize: 2048, bloom: true, particleScale: 1, antialias: true, env: 1, bloomStrength: 0.55, msaa: 4, ao: 'full' },
};

/** Chỉnh màu điện ảnh nhẹ: tương phản + độ bão hòa + tối 4 góc (vignette). Chạy trước OutputPass (không gian tuyến tính). */
const GradeShader = {
  uniforms: { tDiffuse: { value: null }, contrast: { value: 1.05 }, saturation: { value: 1.12 }, vignette: { value: 0.2 } },
  vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
  fragmentShader: `uniform sampler2D tDiffuse; uniform float contrast; uniform float saturation; uniform float vignette; varying vec2 vUv;
    void main(){ vec4 c = texture2D(tDiffuse, vUv); vec3 col = c.rgb;
      float l = dot(col, vec3(0.2126, 0.7152, 0.0722)); col = mix(vec3(l), col, saturation);
      col = (col - 0.18) * contrast + 0.18; col = max(col, 0.0);
      vec2 d = vUv - 0.5; col *= 1.0 - vignette * smoothstep(0.25, 0.75, length(d * vec2(1.15, 1.0)));
      gl_FragColor = vec4(col, c.a); }`,
};
/** Bản đồ môi trường "đêm lễ hội": trời xanh thẫm, chân trời tím, mặt đất ấm + vài mảng đèn lồng vàng cam & một mảng trăng lạnh. Tạo một lần (PMREM). */
function nightEnvironment(renderer: THREE.WebGLRenderer) {
  const sc = new THREE.Scene();
  const geo = new THREE.SphereGeometry(10, 32, 16); const col: number[] = []; const c = new THREE.Color();
  const top = new THREE.Color('#1c2a52'), mid = new THREE.Color('#4a3a5e'), bot = new THREE.Color('#2a1a12');
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) { const y = pos.getY(i) / 10; if (y > 0) c.copy(mid).lerp(top, Math.min(1, y * 1.4)); else c.copy(mid).lerp(bot, Math.min(1, -y * 2)); col.push(c.r, c.g, c.b); }
  geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  sc.add(new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.BackSide })));
  const panel = (color: string, k: number, x: number, y: number, z: number, w: number, h: number) => { const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ color: new THREE.Color(color).multiplyScalar(k), side: THREE.DoubleSide })); m.position.set(x, y, z); m.lookAt(0, 0, 0); sc.add(m); };
  for (let i = 0; i < 6; i++) { const a = (i / 6) * Math.PI * 2; panel('#ffb060', 3.2, Math.cos(a) * 8, 1.2 + (i % 2) * 1.5, Math.sin(a) * 8, 1.6, 1.6); }
  panel('#c8d6ff', 2.4, -4, 7.5, -3, 2.6, 2.6);
  const pm = new THREE.PMREMGenerator(renderer); const tex = pm.fromScene(sc, 0.03).texture; pm.dispose();
  geo.dispose(); sc.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) { m.geometry.dispose(); (m.material as THREE.Material).dispose(); } });
  return tex;
}

/** Kiểm tra WebGL trước khi khởi tạo engine (fallback khi không hỗ trợ). */
export function webglSupport(): { ok: boolean; webgl2: boolean; renderer?: string } {
  try {
    const c = document.createElement('canvas');
    const gl = (c.getContext('webgl2') ?? c.getContext('webgl')) as WebGLRenderingContext | null;
    if (!gl) return { ok: false, webgl2: false };
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    const renderer = ext ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)) : undefined;
    return { ok: true, webgl2: typeof WebGL2RenderingContext !== 'undefined' && gl instanceof WebGL2RenderingContext, renderer };
  } catch { return { ok: false, webgl2: false }; }
}

/** Tự nhận diện mức đồ họa ban đầu (người dùng có thể đổi trong Cài đặt). */
export function detectQuality(): Quality {
  const info = webglSupport();
  const mobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) || matchMedia('(pointer: coarse)').matches;
  const weakGpu = /SwiftShader|llvmpipe|Mali-4|Adreno \(TM\) [23]|PowerVR SGX|Intel\(R\) HD Graphics [2-5]/i.test(info.renderer ?? '');
  const mem = (navigator as { deviceMemory?: number }).deviceMemory ?? 4;
  if (weakGpu || mem <= 2) return 'low';
  if (mobile || mem <= 4) return 'medium';
  return 'high';
}

export type Updatable = (dt: number, t: number) => void;

/** Renderer + vòng lặp + hậu kỳ + đo FPS. Một Engine cho toàn bộ phiên chơi; các scene được thay thế bên trong. */
export class Engine {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  private composer?: EffectComposer;
  private bloom?: UnrealBloomPass;
  private envTex?: THREE.Texture;
  private ao?: N8AOPass; private renderPass?: RenderPass; private smaa?: SMAAPass;
  private readonly clock = new THREE.Clock();
  private readonly updaters = new Set<Updatable>();
  private raf = 0;
  private running = false;
  private frames = 0; private fpsT = performance.now();
  fps = 0;
  profile: QualityProfile;
  /** FPS mục tiêu: 30 | 60 | 120 | 0 (= tối đa theo tần số quét màn hình). */
  targetFps = 0;
  /** Tự hạ/nâng độ phân giải nội bộ để bám FPS mục tiêu. */
  adaptive = true;
  /** Hệ số độ phân giải hiện tại (0.7–1) nhân với pixelRatio của mức đồ họa. */
  renderScale = 1;
  /**
   * v0.13 cân bằng đồ họa: hạ theo bậc, ưu tiên giảm chi phí ẩn (bóng đổ, bloom) trước khi giảm độ phân giải;
   * không bao giờ dưới 70% (trước đây xuống 50% ⇒ mờ nhòe). tier 0 = đầy đủ.
   */
  tier = 0;
  private static readonly TIERS = [
    { scale: 1, shadowEvery: 2, bloom: true, ao: true },
    { scale: 1, shadowEvery: 2, bloom: true, ao: false },
    { scale: 1, shadowEvery: 4, bloom: false, ao: false },
    { scale: 0.85, shadowEvery: 6, bloom: false, ao: false },
    { scale: 0.75, shadowEvery: 10, bloom: false, ao: false },
    { scale: 0.7, shadowEvery: 16, bloom: false, ao: false },
  ];
  private peakFps = 0; private goodT = 0; private lastFrame = 0; private shadowTick = 0;
  private samples: number[] = []; private dropCheck: { from: number; fps: number } | null = null; private tierCap = Engine.TIERS.length - 1; private settleUntil = 0; private holdUntil = 0; private raiseFails = 0; private lastRaiseAt = 0;

  constructor(private readonly host: HTMLElement, public quality: Quality) {
    this.profile = QUALITY[quality];
    this.renderer = new THREE.WebGLRenderer({ antialias: this.profile.antialias, powerPreference: 'high-performance' });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.shadowMap.type = quality === 'high' ? THREE.PCFSoftShadowMap : THREE.PCFShadowMap; /* bóng mềm chỉ ở mức Cao – rẻ hơn nhiều */
    this.renderer.domElement.className = 'game-canvas';
    host.appendChild(this.renderer.domElement);
    this.camera = new THREE.PerspectiveCamera(55, 1, 0.1, 400);
    this.applyQuality(quality);
    window.addEventListener('resize', this.resize);
    this.resize();
  }

  applyQuality(q: Quality) {
    this.quality = q; this.profile = QUALITY[q];
    if ((() => { try { return localStorage.getItem('dn.gfx') === 'legacy'; } catch { return false; } })()) this.profile = { ...this.profile, env: 0, ao: false, bloom: q === 'high', bloomStrength: 0.55, msaa: 0, legacy: true } as QualityProfile; // so sánh đồ họa cũ
    this.renderScale = 1; this.tier = 0; this.samples = []; this.settle(); this.applyPixelRatio();
    this.renderer.shadowMap.enabled = this.profile.shadows;
    this.renderer.shadowMap.autoUpdate = false; // cập nhật bóng thủ công (cách khung) – xem loop
    this.scene.traverse((o) => { const m = (o as THREE.Mesh).material as THREE.Material | undefined; if (m) m.needsUpdate = true; });
    this.composer?.dispose(); this.composer = undefined; this.bloom = undefined; this.ao = undefined; this.renderPass = undefined; this.smaa = undefined;
    // ánh phản chiếu môi trường (rẻ: một texture dựng sẵn) – mọi vật liệu Standard đều có ánh đèn/trăng phản chiếu nhẹ
    this.renderer.toneMappingExposure = (this.profile as { legacy?: boolean }).legacy || q === 'low' ? 1.05 : 1.18; // bù sáng cho AO + vignette
    try { this.envTex ??= nightEnvironment(this.renderer); this.scene.environment = this.profile.env ? this.envTex : null; this.scene.environmentIntensity = this.profile.env; } catch { this.scene.environment = null; }
    if (this.profile.bloom) {
      const rt = new THREE.WebGLRenderTarget(2, 2, { type: THREE.HalfFloatType, samples: this.renderer.capabilities.isWebGL2 ? this.profile.msaa : 0 }); // khử răng cưa cả khi đi qua hậu kỳ
      this.composer = new EffectComposer(this.renderer, rt);
      this.renderPass = new RenderPass(this.scene, this.camera); this.composer.addPass(this.renderPass);
      // che khuất môi trường (AO): góc tường, chân cột, gầm mái tối tự nhiên – cảm giác "thật" rõ nhất; tắt đầu tiên khi máy chậm
      if (this.profile.ao) {
        try {
          const ao = new N8AOPass(this.scene, this.camera, 2, 2);
          ao.configuration.aoRadius = 2.2; ao.configuration.distanceFalloff = 1.2; ao.configuration.intensity = 1.7; ao.configuration.halfRes = this.profile.ao === 'half'; ao.configuration.gammaCorrection = false;
          ao.setQualityMode(this.profile.ao === 'half' ? 'Performance' : 'Medium');
          this.composer.addPass(ao); this.ao = ao; this.renderPass.enabled = false;
        } catch { this.ao = undefined; this.renderPass.enabled = true; }
      }
      this.bloom = new UnrealBloomPass(new THREE.Vector2(256, 256), this.profile.bloomStrength, 0.6, 0.82);
      this.composer.addPass(this.bloom);
      if (!(this.profile as { legacy?: boolean }).legacy) this.composer.addPass(new ShaderPass(GradeShader));
      this.composer.addPass(new OutputPass());
      if (this.ao) { this.smaa = new SMAAPass(2, 2); this.composer.addPass(this.smaa); } // AO vẽ cảnh vào bộ đệm riêng (không MSAA) ⇒ khử răng cưa SMAA
      this.composer.setPixelRatio(this.renderer.getPixelRatio());
    }
    this.resize();
  }

  private applyPixelRatio() {
    this.renderer.setPixelRatio(Math.max(0.5, Math.min(window.devicePixelRatio || 1, this.profile.pixelRatio) * this.renderScale));
    this.composer?.setPixelRatio(this.renderer.getPixelRatio());
    this.resize();
  }

  setTargetFps(n: number) { this.targetFps = n; this.peakFps = 0; this.goodT = 0; this.settle(); }
  setAdaptive(on: boolean) { this.adaptive = on; if (!on) this.setTier(0); }
  /** Bỏ qua đo FPS vài giây (đang tải tầng, đổi cửa sổ…) để không hạ đồ họa oan vì giật tạm thời. */
  settle(ms = 3000, newScene = false) { this.settleUntil = performance.now() + ms; this.samples = []; this.goodT = 0; this.dropCheck = null; if (newScene) this.tierCap = Engine.TIERS.length - 1; }

  private setTier(t: number) {
    const T = Engine.TIERS; t = Math.max(0, Math.min(T.length - 1, t)); this.tier = t;
    const cur = T[t]; if (cur.scale !== this.renderScale) { this.renderScale = cur.scale; this.applyPixelRatio(); }
  }
  private tierDiffers(a: number, b: number) {
    const A = Engine.TIERS[a], B = Engine.TIERS[b];
    return A.scale !== B.scale || (this.profile.shadows && A.shadowEvery !== B.shadowEvery) || (!!this.composer && A.bloom !== B.bloom) || (!!this.ao && A.ao !== B.ao);
  }
  /** Tần số quét màn hình ước lượng (làm tròn về 30/60/75/90/120/144/165). */
  private refresh() { const r = [30, 60, 75, 90, 120, 144, 165]; return r.reduce((a, b) => Math.abs(b - this.peakFps) < Math.abs(a - this.peakFps) ? b : a, 60); }

  /** Mỗi giây: trung bình 2 giây gần nhất thấp hơn 82% mục tiêu ⇒ hạ 1 bậc; ổn định 5 giây ≥ 95% ⇒ thử nâng (thử hỏng ⇒ chờ lâu hơn). */
  private adapt() {
    if (!this.adaptive) return;
    const now = performance.now();
    if (document.hidden || now < this.settleUntil) return;
    this.samples.push(this.fps); if (this.samples.length > 2) this.samples.shift();
    this.peakFps = Math.max(this.peakFps * 0.98, this.fps);
    const goal = Math.min(this.targetFps || 999, this.refresh());
    const avg = this.samples.reduce((a, b) => a + b, 0) / this.samples.length;
    /* vừa hạ 1 bậc mà FPS không khá hơn (máy nghẽn ở CPU, không phải đồ họa) ⇒ trả lại bậc cũ, không hạ thêm – tránh mờ hình vô ích */
    if (this.dropCheck && this.samples.length >= 2) {
      const dc = this.dropCheck; this.dropCheck = null;
      if (avg < dc.fps * 1.08) { this.tierCap = dc.from; this.setTier(dc.from); this.samples = []; this.holdUntil = now + 120_000; return; }
    }
    if (this.samples.length >= 2 && avg < goal * 0.82 && this.tier < this.tierCap) {
      if (now - this.lastRaiseAt < 6000) this.raiseFails++; /* vừa nâng đã tụt lại ⇒ lần sau chờ lâu hơn */
      this.dropCheck = { from: this.tier, fps: avg };
      let next = this.tier + 1; while (next < this.tierCap && !this.tierDiffers(this.tier, next)) next++; /* bỏ qua bậc không đổi gì với mức đồ họa này (vd mức Thấp không có bóng/bloom) */
      this.setTier(next); this.samples = []; this.goodT = 0;
      this.holdUntil = now + Math.min(120_000, 8000 * 2 ** this.raiseFails);
    } else if (this.fps >= goal * 0.95 && this.tier > 0 && now > this.holdUntil) {
      if (++this.goodT >= 5) { this.setTier(this.tier - 1); this.goodT = 0; this.lastRaiseAt = now; this.samples = []; }
    } else if (this.fps < goal * 0.95) this.goodT = 0;
  }

  private resize = () => {
    const w = this.host.clientWidth || window.innerWidth; const h = this.host.clientHeight || window.innerHeight;
    this.camera.aspect = w / h; this.camera.fov = w < h ? 68 : 55; this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
    this.composer?.setSize(w, h);
    // bloom ở nửa độ phân giải: nhìn gần như nhau, rẻ hơn ~4 lần
    if (this.bloom) this.bloom.setSize(Math.ceil(w / 2), Math.ceil(h / 2));
    const pr = this.renderer.getPixelRatio(); this.ao?.setSize(Math.round(w * pr), Math.round(h * pr)); this.smaa?.setSize(Math.round(w * pr), Math.round(h * pr));
  };

  add(fn: Updatable) { this.updaters.add(fn); return () => this.updaters.delete(fn); }

  start() {
    if (this.running) return;
    this.running = true; this.clock.start();
    const loop = () => {
      if (!this.running) return;
      this.raf = requestAnimationFrame(loop);
      // Giới hạn FPS (vd 60 trên màn 144Hz để mát máy); 0 = chạy theo tần số quét
      const nowF = performance.now();
      if (this.targetFps > 0 && nowF - this.lastFrame < 1000 / this.targetFps - 1.5) return;
      this.lastFrame = nowF;
      // Bóng đổ: cập nhật cách một khung khi FPS cao (mắt không thấy, tiết kiệm ~1 lượt vẽ cảnh)
      const tierCfg = Engine.TIERS[this.tier];
      if (this.profile.shadows) this.renderer.shadowMap.needsUpdate = (this.shadowTick++ % tierCfg.shadowEvery) === 0;
      const raw = this.clock.getDelta(); if (raw > 0.25 && this.fps >= 20) this.settle(2000); /* khựng đơn lẻ (tải tầng, đổi tab) ⇒ không tính; máy yếu thường xuyên chậm thì vẫn tự hạ */
      const dt = Math.min(raw, 0.1); const t = this.clock.elapsedTime;
      for (const u of this.updaters) u(dt, t);
      if (this.composer && (tierCfg.bloom || (this.ao && tierCfg.ao))) {
        const aoOn = !!this.ao && tierCfg.ao; if (this.ao) { this.ao.enabled = aoOn; this.renderPass!.enabled = !aoOn; if (this.smaa) this.smaa.enabled = aoOn; }
        if (this.bloom) this.bloom.enabled = tierCfg.bloom;
        this.composer.render();
      } else this.renderer.render(this.scene, this.camera);
      // FPS theo thời gian thực (không dùng dt đã kẹp)
      this.frames++; const now = performance.now();
      if (now - this.fpsT >= 1000) { this.fps = Math.round((this.frames * 1000) / (now - this.fpsT)); this.frames = 0; this.fpsT = now; this.adapt(); }
    };
    loop();
  }

  /** Chụp khung hình hiện tại (vẽ lại rồi đọc ngay – không cần preserveDrawingBuffer). */
  capture(): HTMLCanvasElement {
    if (this.composer) this.composer.render(); else this.renderer.render(this.scene, this.camera);
    const src = this.renderer.domElement; const c = document.createElement('canvas'); c.width = src.width; c.height = src.height;
    c.getContext('2d')!.drawImage(src, 0, 0); return c;
  }
  stop() { this.running = false; cancelAnimationFrame(this.raf); }

  /** Thông số bộ nhớ GPU thật từ renderer (dùng cho kiểm thử hiệu năng). */
  stats() { const i = this.renderer.info; return { fps: this.fps, renderScale: this.renderScale, targetFps: this.targetFps, geometries: i.memory.geometries, textures: i.memory.textures, calls: i.render.calls, triangles: i.render.triangles }; }

  dispose() { this.stop(); window.removeEventListener('resize', this.resize); this.composer?.dispose(); this.renderer.dispose(); this.renderer.domElement.remove(); }
}

/** Giải phóng mọi geometry/material/texture trong một nhánh scene khi rời tầng. */
export function disposeTree(root: THREE.Object3D) {
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    m.geometry?.dispose();
    const mats = Array.isArray(m.material) ? m.material : m.material ? [m.material] : [];
    for (const mat of mats) {
      if (mat.userData?.shared) continue; // v0.19: vật liệu dùng chung (nhà ở) sống suốt phiên
      for (const v of Object.values(mat)) if (v instanceof THREE.Texture && !v.userData?.shared) v.dispose();
      mat.dispose();
    }
  });
  root.removeFromParent();
}
